<script setup>
  import {inject, onMounted, ref, watch} from "vue";

  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import {usePage} from "@inertiajs/vue3";
  import MergeAccountIconGroup from "@pages/MergeAccount/Components/IconGroup.vue";
  import MergeAccountStep from "@pages/MergeAccount/Components/Step.vue";
  import MergeAccountStepDivider from "@pages/MergeAccount/Components/StepDivider.vue";
  import MergeAccountSteps from "@pages/MergeAccount/Components/Steps.vue";
  import {debounce} from "es-toolkit";

  import Crypt from "@/resources/js/crypt.js";

  defineOptions({name: "MergeAccountShowStepsPrivacyPassword"});

  const emit = defineEmits(["ready"]);

  const privacy = inject("$privacy");
  const pageProperties = usePage().props;

  const refSourceKey = ref(null);
  const refTargetKey = ref(null);

  const sourcePlaintext = ref(null);
  const targetPlaintext = ref(null);

  const sourceKey = ref(null);
  const targetKey = ref(null);

  const sourceValidation = ref(null);
  const targetValidation = ref(null);

  onMounted(() => {
    // which key do we already know
    // from the logged-in user?
    // source...
    if (privacy.getClient()?.id === pageProperties.source.id) {
      sourceKey.value = privacy.getRecoveryCode().split(" ").join("");
    }
    // ...or target?
    if (privacy.getClient()?.id === pageProperties.target.id) {
      targetKey.value = privacy.getRecoveryCode().split(" ").join("");
    }

    if (sourceKey.value === null) {
      refSourceKey.value.focus();
    } else if (targetKey.value === null) {
      refTargetKey.value.focus();
    }
  });

  watch(
    () => sourcePlaintext.value,
    debounce((plaintextPassword) => {
      const temporaryCrypt = new Crypt("");

      // collect all users.userencryption in a single array
      const userEncryptions = pageProperties.source.users.map((user) => user.userencryption);

      temporaryCrypt.sha256(plaintextPassword).then((hashedPassword) => {
        const checkCrypt = new Crypt(hashedPassword);

        Promise.any(userEncryptions.map((value) => checkCrypt.enableStrictMode().decrypt(value)))
          .then((decrypted) => {
            sourceKey.value = decrypted;
          })
          .catch((error) => {
            console.error("Error validating source privacy password:", error);
            sourceValidation.value = "Das Datenschutzpasswort ist nicht korrekt.";
          });
      });
    }, 1000),
  );

  watch(
    () => targetPlaintext.value,
    debounce((plaintextPassword) => {
      const temporaryCrypt = new Crypt("");

      // collect all users.userencryption in a single array
      const userEncryptions = pageProperties.target.users.map((user) => user.userencryption);

      temporaryCrypt.sha256(plaintextPassword).then((hashedPassword) => {
        const checkCrypt = new Crypt(hashedPassword);

        Promise.any(userEncryptions.map((value) => checkCrypt.enableStrictMode().decrypt(value)))
          .then((decrypted) => {
            targetKey.value = decrypted;
          })
          .catch((error) => {
            console.error("Error validating target privacy password:", error);
            targetValidation.value = "Das Datenschutzpasswort ist nicht korrekt.";
          });
      });
    }, 1000),
  );

  watch([() => sourceKey.value, () => targetKey.value], ([newSourceKey, newTargetKey]) => {
    if (newSourceKey !== null && newTargetKey !== null) {
      emit("ready", {sourceKey: newSourceKey, targetKey: newTargetKey});
    }
  });
</script>

<template>
  <MergeAccountSteps>
    <MergeAccountStep :current-account="$page.props.client.id === $page.props.source.id">
      <MergeAccountIconGroup>
        <ComponentIcon v-if="$page.props.source.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
        <ComponentIcon v-else class="shrink-0" custom="front-desk" />
      </MergeAccountIconGroup>

      <div class="mt-6">
        <h3 class="text-xl font-semibold">
          <span v-if="$page.props.source.third_party_type === 'p4u'">pharma4u</span>
          <span v-else>Warenwirtschaft</span>
        </h3>

        <div v-if="sourceKey === null">
          <ComponentInput
            ref="refSourceKey"
            v-model="sourcePlaintext"
            :validation="sourceValidation"
            label="Datenschutzpasswort"
            type="password"
          />
        </div>

        <div v-else>
          <ComponentInput
            label="Datenschutzpasswort"
            disabled
            hide-clear
            type="text"
            helper-text="Passwort liegt vor"
            model-value="••••••••"
          >
            <template #icon>
              <ComponentIcon class="text-xl text-mcgreen">check</ComponentIcon>
            </template>
          </ComponentInput>
        </div>
      </div>
    </MergeAccountStep>

    <MergeAccountStepDivider>
      <ComponentIcon class="text-4xl">add</ComponentIcon>
    </MergeAccountStepDivider>

    <MergeAccountStep :current-account="$page.props.client.id === $page.props.target.id">
      <MergeAccountIconGroup>
        <ComponentIcon v-if="$page.props.target.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
        <ComponentIcon v-else class="shrink-0" custom="front-desk" />
      </MergeAccountIconGroup>

      <div class="mt-6">
        <h3 class="text-xl font-semibold">
          <span v-if="$page.props.target.third_party_type === 'p4u'">pharma4u</span>
          <span v-else>Warenwirtschaft</span>
        </h3>

        <div v-if="targetKey === null">
          <ComponentInput
            ref="refTargetKey"
            v-model="targetPlaintext"
            :validation="targetValidation"
            label="Datenschutzpasswort"
            type="password"
          />
        </div>
        <div v-else>
          <ComponentInput
            label="Datenschutzpasswort"
            disabled
            hide-clear
            type="text"
            helper-text="Passwort liegt vor"
            model-value="••••••••"
          >
            <template #icon>
              <ComponentIcon class="text-xl text-mcgreen">check</ComponentIcon>
            </template>
          </ComponentInput>
        </div>

        <div class="mt-4 text-sm font-semibold">
          <ComponentIcon class="inline-block">lock</ComponentIcon>
          Dieses Datenschutzpasswort wird zukünftig für beide Accounts verwendet
        </div>
      </div>
    </MergeAccountStep>

    <MergeAccountStepDivider>
      <ComponentIcon class="rotate-90 text-4xl md:rotate-0">trending_flat</ComponentIcon>
    </MergeAccountStepDivider>

    <MergeAccountStep>
      <MergeAccountIconGroup>
        <ComponentIcon v-if="$page.props.source.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
        <ComponentIcon v-else class="shrink-0" custom="front-desk" />
        <ComponentIcon v-if="$page.props.target.third_party_type === 'p4u'" class="shrink-0" custom="laptop" />
        <ComponentIcon v-else class="shrink-0" custom="front-desk" />
      </MergeAccountIconGroup>
      <div class="mt-6">
        <h3 class="text-xl font-semibold">Verknüpfter Account</h3>

        <div class="mt-4 text-sm font-semibold">
          <ComponentIcon class="inline-block">lock</ComponentIcon>
          Sie nutzen in Zukunft für beide Accounts das gleiche Datenschutzpasswort.
        </div>
      </div>
    </MergeAccountStep>
  </MergeAccountSteps>
</template>
